import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiGlobe, FiX, FiPlus, FiCheckCircle, FiSlash } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const TRANSACTION_TYPES = ['TRANSFER', 'MANAGEMENT_FEE', 'LOAN', 'COST_RECHARGE'];

const emptyForm = () => ({
  from_entity: '', to_entity: '', transaction_type: 'MANAGEMENT_FEE', amount: '', currency: '',
  transaction_date: '', justification: '', from_debit_account: '', from_credit_account: '',
  to_debit_account: '', to_credit_account: '',
});

const IntercompanyTransactions = () => {
  const { isChairman, user } = useAuth();
  const isCfo = user?.role?.toUpperCase() === 'CFO';
  const [entities, setEntities] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, curRes, accRes, txRes] = await Promise.all([
        financeAPI.entities.list(), financeAPI.currencies.list(), financeAPI.accounts.list(), financeAPI.intercompany.list(),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setCurrencies(curRes.data.results || curRes.data || []);
      setAccounts(accRes.data.results || accRes.data || []);
      setTransactions(txRes.data.results || txRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load intercompany transactions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.intercompany.create(form);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create intercompany transaction.');
    } finally {
      setSaving(false);
    }
  };

  const approve = async (tx, as) => {
    const code = window.prompt('This is a high-risk action — enter your current authenticator code to confirm:');
    if (!code) return;
    setBusyId(tx.id);
    setError(null);
    try {
      if (as === 'chairman') await financeAPI.intercompany.approveChairman(tx.id, code);
      else await financeAPI.intercompany.approveCfo(tx.id, code);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Approval failed.');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (tx) => {
    const reason = window.prompt('Reason for rejecting this intercompany transaction?');
    if (!reason) return;
    const code = window.prompt('This is a high-risk action — enter your current authenticator code to confirm rejection:');
    if (!code) return;
    setBusyId(tx.id);
    setError(null);
    try {
      await financeAPI.intercompany.reject(tx.id, reason, code);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to reject.');
    } finally {
      setBusyId(null);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || '—';
  const accountsFor = (entityId) => accounts.filter((a) => !entityId || String(a.entity) === String(entityId) || !a.entity);

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiGlobe size={22} />Intercompany Transactions</h1>
          <p style={fs.pageSubtitle}>Between İzmir and Zimbabwe. Requires written justification and dual sign-off — Chairman AND CFO, not either/or.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Transaction</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div><div style={fs.label}>From Entity</div>
            <select required value={form.from_entity} onChange={(e) => setForm({ ...form, from_entity: e.target.value, from_debit_account: '', from_credit_account: '' })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>To Entity</div>
            <select required value={form.to_entity} onChange={(e) => setForm({ ...form, to_entity: e.target.value, to_debit_account: '', to_credit_account: '' })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Type</div>
            <select value={form.transaction_type} onChange={(e) => setForm({ ...form, transaction_type: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              {TRANSACTION_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Amount (USD only)</div><input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div><div style={fs.label}>Currency</div>
            <select required value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{currencies.filter((c) => c.code === 'USD').map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Transaction Date</div><input required type="date" value={form.transaction_date} onChange={(e) => setForm({ ...form, transaction_date: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={fs.label}>Justification (required — this is what a tax advisor reads first)</div>
            <textarea required value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} rows={2} style={{ ...fs.input, width: '100%', fontFamily: 'inherit', resize: 'vertical' }} />
          </div>
          <div><div style={fs.label}>From: Debit Account</div>
            <select required value={form.from_debit_account} onChange={(e) => setForm({ ...form, from_debit_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{accountsFor(form.from_entity).map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>From: Credit Account</div>
            <select required value={form.from_credit_account} onChange={(e) => setForm({ ...form, from_credit_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{accountsFor(form.from_entity).map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>To: Debit Account</div>
            <select required value={form.to_debit_account} onChange={(e) => setForm({ ...form, to_debit_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{accountsFor(form.to_entity).map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>To: Credit Account</div>
            <select required value={form.to_credit_account} onChange={(e) => setForm({ ...form, to_credit_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Select…</option>{accountsFor(form.to_entity).map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Submit Request'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : transactions.length === 0 ? (
        <div style={fs.emptyState}><FiGlobe size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No intercompany transactions yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {transactions.map((tx) => {
            const busy = busyId === tx.id;
            const color = fs.STATUS_COLORS[tx.status] || '#888';
            return (
              <div key={tx.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{entityName(tx.from_entity)} → {entityName(tx.to_entity)}</span>
                      <span style={fs.badge(color)}>{tx.status}</span>
                      <span style={{ color: fs.colors.textBright, fontWeight: 700 }}>{fs.money(tx.amount, tx.currency)}</span>
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>{tx.transaction_type.replace('_', ' ')} · {tx.transaction_date}</div>
                    <div style={{ color: fs.colors.textDim, fontSize: 12, marginTop: 6, fontStyle: 'italic' }}>&ldquo;{tx.justification}&rdquo;</div>
                    {tx.status === 'PENDING' && (
                      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11 }}>
                        <span style={{ color: tx.chairman_approved_by ? fs.colors.green : fs.colors.textFaint }}>{tx.chairman_approved_by ? '✓' : '○'} Chairman sign-off</span>
                        <span style={{ color: tx.cfo_approved_by ? fs.colors.green : fs.colors.textFaint }}>{tx.cfo_approved_by ? '✓' : '○'} CFO sign-off</span>
                      </div>
                    )}
                    {tx.status === 'REJECTED' && tx.rejection_reason && (
                      <div style={{ color: fs.colors.red, fontSize: 12, marginTop: 6 }}>Rejected: {tx.rejection_reason}</div>
                    )}
                  </div>
                  {tx.status === 'PENDING' && (
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {isChairman() && !tx.chairman_approved_by && (
                        <button onClick={() => approve(tx, 'chairman')} disabled={busy} style={fs.successButton}><FiCheckCircle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Approve (Chairman)</button>
                      )}
                      {isCfo && !tx.cfo_approved_by && (
                        <button onClick={() => approve(tx, 'cfo')} disabled={busy} style={fs.successButton}><FiCheckCircle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Approve (CFO)</button>
                      )}
                      <button onClick={() => reject(tx)} disabled={busy} style={fs.dangerButton}><FiSlash size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Reject</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IntercompanyTransactions;
